import { motion } from "framer-motion";
import { Clock, CreditCard, FileText, AlertCircle, CheckCircle2, Sparkles, List } from "lucide-react";
import { loadPayLaterSessionForSlug } from "../../utils/conferencePayLaterSession";

export default function PayLaterState({ 
  applicationId, 
  eventDetails,
  registrationData,
  slug,
  locationState,
  onNavigate 
}) {
  const handleCompletePayment = () => {
    const loc = locationState || {};
    const session = slug ? loadPayLaterSessionForSlug(slug) : null;
    const sess = session || {};
    // Prefer eventData from location.state (passed from ConferencePayment) so we keep the same structure
    const eventData =
      loc.eventData ??
      (eventDetails ? (eventDetails?.data?.event ?? eventDetails?.event ?? eventDetails) : null) ??
      sess.eventData;
    onNavigate(`/event-details/${slug}/payment`, {
      state: {
        registrationData: registrationData ?? sess.registrationData,
        eventData: eventData ?? sess.eventData,
        slug,
        payLaterEntry: true,
        isGroup: loc.isGroup ?? sess.isGroup,
        numberOfAttendees: loc.numberOfAttendees ?? sess.numberOfAttendees,
        totalAmount: loc.totalAmount ?? sess.totalAmount,
        attendees: loc.attendees ?? sess.attendees,
        selectedTicket: loc.selectedTicket ?? sess.selectedTicket,
      },
    });
  };

  const handleGoToRegistrations = () => {
    onNavigate("/conference-registrations");
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.5 }}
    >
      <div className="text-center mb-10">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="mb-3 text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-orange-900 to-amber-900 sm:text-5xl">
            Registration Submitted!
          </h1>
        </motion.div>

        {applicationId && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="inline-flex flex-wrap items-center justify-center gap-x-2 gap-y-1 mb-5 px-3 py-2 sm:px-6 sm:py-3 max-w-full bg-gradient-to-r from-orange-100 to-amber-100 rounded-full border-2 border-orange-300 shadow-lg"
          >
            <FileText size={18} className="shrink-0 text-orange-700" />
            <span className="text-xs font-semibold text-gray-700 sm:text-sm shrink-0">Application ID:</span>
            <span className="font-mono font-bold text-sm text-orange-900 break-all min-w-0 sm:text-lg">{applicationId}</span>
          </motion.div>
        )}

        {eventDetails?.event_title && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.25 }}
            className="mb-10"
          >
            <p className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">Event</p>
            <p className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-amber-600">
              {eventDetails.event_title}
            </p>
          </motion.div>
        )}

        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.3, type: "spring", stiffness: 150, damping: 10 }}
          className="flex justify-center mb-8"
        >
          <div className="relative">
            <motion.div 
              className="flex justify-center items-center w-36 h-36 bg-gradient-to-br from-amber-400 via-orange-500 to-amber-600 rounded-full border-4 border-amber-300 shadow-2xl"
              animate={{ 
                boxShadow: [
                  "0 20px 40px rgba(251, 191, 36, 0.4)",
                  "0 25px 50px rgba(251, 191, 36, 0.6)",
                  "0 20px 40px rgba(251, 191, 36, 0.4)"
                ]
              }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <Clock size={72} className="text-white drop-shadow-2xl" />
            </motion.div>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: [1, 1.3, 1] }}
              transition={{ delay: 0.5, duration: 2, repeat: Infinity }}
              className="absolute inset-0 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full opacity-20"
            />
            <motion.div
              animate={{ 
                scale: [1, 1.5, 1],
                opacity: [0.3, 0, 0.3]
              }}
              transition={{ duration: 3, repeat: Infinity }}
              className="absolute inset-0 border-4 border-amber-400 rounded-full"
            />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="mb-4"
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Payment Pending</h2>
          <div className="flex items-center justify-center gap-2">
            <motion.div
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="w-2 h-2 bg-amber-500 rounded-full"
            />
            <span className="text-sm font-semibold text-amber-700 uppercase tracking-wide">Action Required</span>
            <motion.div
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 1.5, repeat: Infinity, delay: 0.5 }}
              className="w-2 h-2 bg-amber-500 rounded-full"
            />
          </div>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mb-10 text-base text-gray-600 max-w-2xl mx-auto leading-relaxed"
        >
          Your registration has been <span className="font-semibold text-gray-900">successfully saved</span>, but payment is still required to complete your booking. 
          Complete your payment now to <span className="font-semibold text-orange-700">secure your spot</span> at the event.
        </motion.p>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="grid gap-5 md:grid-cols-2 mb-8 max-w-4xl mx-auto"
      >
        <motion.button
          onClick={handleCompletePayment}
          whileHover={{ scale: 1.03, y: -4 }}
          whileTap={{ scale: 0.97 }}
          className="relative overflow-hidden group p-8 bg-gradient-to-br from-orange-500 via-orange-600 to-orange-700 rounded-2xl shadow-2xl transition-all hover:shadow-orange-500/50"
        >
          <motion.div 
            className="absolute inset-0 bg-gradient-to-br from-white/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"
            animate={{
              backgroundPosition: ['0% 0%', '100% 100%'],
            }}
            transition={{ duration: 3, repeat: Infinity, repeatType: 'reverse' }}
          />
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-3xl group-hover:scale-150 transition-transform duration-500" />
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-orange-400/20 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-500" />
          
          <div className="relative flex flex-col items-center gap-4">
            <motion.div 
              className="flex justify-center items-center w-16 h-16 bg-white/25 backdrop-blur-md rounded-2xl shadow-lg border border-white/30"
              whileHover={{ rotate: [0, -10, 10, -10, 0] }}
              transition={{ duration: 0.5 }}
            >
              <CreditCard size={32} className="text-white drop-shadow-lg" />
            </motion.div>
            <div className="text-center">
              <h3 className="text-xl font-bold text-white mb-2 drop-shadow-md">Pay Now</h3>
              <p className="text-sm text-orange-50 leading-relaxed">Complete your payment and secure your spot at the event</p>
            </div>
            <motion.div 
              className="flex items-center gap-2 mt-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full border border-white/30"
              animate={{ x: [0, 5, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <span className="text-xs font-bold text-white uppercase tracking-widest">Continue</span>
              <motion.span className="text-white text-lg">→</motion.span>
            </motion.div>
          </div>
        </motion.button>

        <motion.button
          onClick={handleGoToRegistrations}
          whileHover={{ scale: 1.03, y: -4 }}
          whileTap={{ scale: 0.97 }}
          className="relative overflow-hidden group p-8 bg-gradient-to-br from-slate-50 to-white rounded-2xl shadow-xl border-2 border-gray-200 transition-all hover:border-purple-400 hover:shadow-2xl"
        >
          <motion.div 
            className="absolute inset-0 bg-gradient-to-br from-purple-50 via-transparent to-blue-50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          />
          <div className="absolute top-0 right-0 w-32 h-32 bg-purple-100/40 rounded-full blur-3xl group-hover:scale-150 transition-transform duration-500" />
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-blue-100/30 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-500" />
          
          <div className="relative flex flex-col items-center gap-4">
            <motion.div 
              className="flex justify-center items-center w-16 h-16 bg-gradient-to-br from-purple-100 to-blue-100 rounded-2xl shadow-md group-hover:from-purple-200 group-hover:to-blue-200 transition-all duration-300 border border-purple-200"
              whileHover={{ rotate: [0, -10, 10, -10, 0] }}
              transition={{ duration: 0.5 }}
            >
              <List size={32} className="text-purple-600 drop-shadow-sm" />
            </motion.div>
            <div className="text-center">
              <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-purple-900 transition-colors">My Conference Registrations</h3>
              <p className="text-sm text-gray-600 group-hover:text-gray-700 leading-relaxed">View and manage all your conference bookings</p>
            </div>
            <motion.div 
              className="flex items-center gap-2 mt-2 px-4 py-2 bg-gradient-to-r from-purple-50 to-blue-50 rounded-full border border-purple-200 group-hover:border-purple-300 transition-colors"
              animate={{ x: [0, 5, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <span className="text-xs font-bold text-purple-700 uppercase tracking-widest">View All</span>
              <motion.span className="text-purple-600 text-lg">→</motion.span>
            </motion.div>
          </div>
        </motion.button>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8 }}
        className="grid gap-5 md:grid-cols-3 max-w-5xl mx-auto"
      >
        <motion.div 
          className="relative overflow-hidden p-5 bg-gradient-to-br from-emerald-50 to-green-50 rounded-2xl border-2 border-emerald-200 shadow-lg hover:shadow-xl transition-all group"
          whileHover={{ y: -2 }}
        >
          <div className="absolute top-0 right-0 w-20 h-20 bg-emerald-200/30 rounded-full blur-2xl" />
          <div className="relative flex items-start gap-4">
            <motion.div 
              className="flex-shrink-0 p-3 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl shadow-md group-hover:scale-110 transition-transform"
              whileHover={{ rotate: 360 }}
              transition={{ duration: 0.6 }}
            >
              <CheckCircle2 size={24} className="text-white" />
            </motion.div>
            <div>
              <h4 className="text-base font-bold text-emerald-900 mb-1.5">Registration Saved</h4>
              <p className="text-sm text-emerald-700 leading-relaxed">Your details are securely stored and protected</p>
            </div>
          </div>
        </motion.div>

        <motion.div 
          className="relative overflow-hidden p-5 bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl border-2 border-amber-300 shadow-lg hover:shadow-xl transition-all group"
          whileHover={{ y: -2 }}
        >
          <div className="absolute top-0 right-0 w-20 h-20 bg-amber-200/30 rounded-full blur-2xl" />
          <div className="relative flex items-start gap-4">
            <motion.div 
              className="flex-shrink-0 p-3 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl shadow-md group-hover:scale-110 transition-transform"
              animate={{ rotate: [0, 10, 0, -10, 0] }}
              transition={{ duration: 3, repeat: Infinity }}
            >
              <Clock size={24} className="text-white" />
            </motion.div>
            <div>
              <h4 className="text-base font-bold text-amber-900 mb-1.5">Payment Pending</h4>
              <p className="text-sm text-amber-700 leading-relaxed">Complete payment to confirm your booking</p>
            </div>
          </div>
        </motion.div>

        <motion.div 
          className="relative overflow-hidden p-5 bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl border-2 border-purple-300 shadow-lg hover:shadow-xl transition-all group"
          whileHover={{ y: -2 }}
        >
          <div className="absolute top-0 right-0 w-20 h-20 bg-purple-200/30 rounded-full blur-2xl" />
          <div className="relative flex items-start gap-4">
            <motion.div 
              className="flex-shrink-0 p-3 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl shadow-md group-hover:scale-110 transition-transform"
              animate={{ scale: [1, 1.1, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <Sparkles size={24} className="text-white" />
            </motion.div>
            <div>
              <h4 className="text-base font-bold text-purple-900 mb-1.5">Limited Spots</h4>
              <p className="text-sm text-purple-700 leading-relaxed">Secure your spot before it's too late</p>
            </div>
          </div>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1 }}
        className="mt-8 relative overflow-hidden p-6 bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50 rounded-2xl border-2 border-orange-300 shadow-xl max-w-4xl mx-auto"
      >
        <div className="absolute top-0 right-0 w-40 h-40 bg-orange-200/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-amber-200/20 rounded-full blur-2xl" />
        
        <div className="relative flex gap-4 items-start">
          <motion.div 
            className="flex-shrink-0 p-3 bg-gradient-to-br from-orange-500 to-amber-600 rounded-xl shadow-lg"
            animate={{ 
              boxShadow: [
                "0 4px 6px -1px rgba(249, 115, 22, 0.3)",
                "0 10px 15px -3px rgba(249, 115, 22, 0.5)",
                "0 4px 6px -1px rgba(249, 115, 22, 0.3)"
              ]
            }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <AlertCircle size={28} className="text-white" />
          </motion.div>
          <div className="flex-1">
            <h3 className="text-lg font-bold text-orange-900 mb-3 flex items-center gap-2">
              Important Notice
              <motion.span
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 1.5, repeat: Infinity }}
                className="inline-block w-2 h-2 bg-orange-500 rounded-full"
              />
            </h3>
            <p className="text-sm leading-relaxed text-orange-800 mb-3">
              Your registration will remain <span className="font-bold text-orange-900">pending until payment is completed</span>. 
              We recommend completing your payment as soon as possible to guarantee your spot at the event.
            </p>
            <div className="flex items-start gap-2 text-xs text-orange-700 bg-white/50 p-3 rounded-lg border border-orange-200">
              <CheckCircle2 size={16} className="flex-shrink-0 mt-0.5 text-orange-600" />
              <span>You can access your registration details anytime and complete payment later from your dashboard.</span>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

